import EmailRequiredBanner from "@/components/EmailRequiredBanner.tsx";
import ImpersonationBanner from "@/components/ImpersonationBanner.tsx";
import {AppSidebar} from "@/components/AppSidebar.tsx";
import SupportChatButton from "@/components/SupportChatButton.tsx";
import ClaimProfileModal from "@/components/modals/ClaimProfileModal.tsx";
import {SidebarInset, SidebarProvider} from "@/components/ui/sidebar.tsx";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, Navigate, Outlet, useLocation} from "@tanstack/react-router"
import EventPassReturnConfirm from "../components/events/EventPassReturnConfirm.tsx";
import BillingStatusBanner from "../components/BillingStatusBanner.tsx";
import {useSessionStorage, useWindowSize} from "@uidotdev/usehooks";
import dayjs from "dayjs";
import {driver} from "driver.js";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {apiActionImproved} from "../api.ts";
import {sportAreaForUser, sportAreaHome} from "../sports/sportAreas.ts";
import AdSensePush from "../components/AdSensePush.tsx";
import WelcomeRankModal from "../components/modals/WelcomeRankModal.tsx";
import WelcomeTutorialModal from "../components/modals/WelcomeTutorialModal.tsx";
import WhatsAppSessionsWelcome, {markWhatsAppSessionsSeen} from "../sessions/WhatsAppSessionsWelcome.tsx";
import PButton from "../components/PButton.tsx";
import {config} from "../config/config.ts";
import {UserRole, UserStatus} from "../constants.ts";
import PaymentsSetupWizard from "../components/modals/PaymentsSetupWizard.tsx";
import {PAYMENTS_PROMPT_SESSION_KEY, PAYMENTS_SETUP_RETURN_PARAM, paymentsSetupPromptDue} from "../onboarding/paymentsSetupPrompt.ts";
import OrganizerWelcomeModal from "../components/onboarding/OrganizerWelcomeModal.tsx";
import {WELCOME_CARDS_SESSION_KEY, welcomeCardsPopupDue} from "../onboarding/welcomeCards.ts";
import {useGlobalModal} from "../core/GlobalModal.tsx";
import {usePusher} from "../core/PusherContext.tsx";
import Header from "../Header.tsx";
import {usePullToRefresh} from "../hooks/usePullToRefresh.tsx";
import {useSubscription} from "../hooks/useSubscription.ts";
import {useJwtAuth} from "../JWTAuthProvider.tsx";
import Private404Page from "../Private404Page.tsx";
import PassGateListener from "../components/modals/PassGateListener.tsx";
import QuizWelcomeTour from "../components/QuizWelcomeTour.tsx";
import User_ProfileView_PlayerQuestionnaire from "../user/User_ProfileView_PlayerQuestionnaire.tsx";
import "driver.js/dist/driver.css";
import {footballV2On, racquetOnboarding, welcomeRankDue} from "@/onboarding/plays.ts";
import {useSports} from "@/sports/registry.ts";

const MAXIMUM_PULL_LENGTH = 240;
const REFRESH_THRESHOLD = 180;

export const Route = createFileRoute('/_app')({
    component: RouteComponent,
    ssr: false,

    notFoundComponent: () => (
        <Private404Page />
    ),
})

// Using shared AdSensePush component from AdSenseAd.tsx

function RouteComponent() {
    const jwt = useJwtAuth();
    const user = jwt.user;
    const {isSubscribed} = useSubscription();
    const pusher = usePusher();
    const queryClient = useQueryClient();
    const globalModal = useGlobalModal();
    const [lastReloadTs, setLastReloadTs] = useState(0);
    const [welcomeTourShown, setWelcomeTourShown] = useState(false);
    const location = useLocation();
    
    if (sessionStorage?.welcome_tutorial_seen === 'y') { // convert value to useSessionStorage format
        sessionStorage.welcome_tutorial_seen = '"y"';
    }
    const [welcomeTutorialSeen, setWelcomeTutorialSeen] = useSessionStorage("welcome_tutorial_seen", '');
    const welcomeModalOpenedRef = useRef(false);
    const welcomeRankOpenedRef = useRef(false);
    const whatsAppWelcomeOpenedRef = useRef(false);
    const paymentsPromptOpenedRef = useRef(false);
    const welcomeCardsOpenedRef = useRef(false);
    const playsLoadRef = useRef(false);
    // The football v2 switch: the racquet welcome and questionnaire follow it (onboarding/plays).
    const {data: registry} = useSports();

    const size = useWindowSize();
    const isMobile = (size.width && size.width <= 767); // matches useSidebar.isMobile behaviour

    // Redirect for unauthenticated visitors, built once and reused.
    //
    // The WHOLE ELEMENT must be memoised, not just the search value. <Navigate> guards its
    // navigation on props *object identity*:
    //
    //     if (previousPropsRef.current !== props) { navigate(props); ... }
    //
    // JSX allocates a fresh props object on every render, so that guard never holds. Each render
    // re-issues the navigation -> commitLocation -> Transitioner's setIsTransitioning -> re-render
    // -> navigate again, until React aborts with "Maximum update depth exceeded" (minified #185,
    // logged in production and confirmed by source-mapping the deployed stack). Holding one
    // element keeps a single props object alive, so the redirect fires exactly once.
    //
    // window.location is read here rather than during the render below for the same reason: it
    // changes as the navigation proceeds, so the captured target would otherwise never settle.
    const loginRedirect = useMemo(
        () => (
            <Navigate
                to={'/login/'}
                search={{redirect: typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/'}}
            />
        ),
        [],
    );

    // Quiz arrivals (?welcome=quiz): the one-time tour, and the WhatsApp welcome held back a page.
    // The sidebar is shown to them as to any other organiser.
    const quizFocus = typeof window !== 'undefined'
        && new URLSearchParams(location.searchStr || window.location.search).get('welcome') === 'quiz';

    const versionQuery = useQuery({
        queryKey: ['version'],
        queryFn:  async () => await apiActionImproved('version' as any).then((r) => r.text()),
    })

    const handler = (event) => {
        if (event.table === 'messages') {
            jwt.reloadUser();
            if (!document.location.href.includes('/messages')) {
                addToast({
                    title:       (event.data._sender_name ? 'New message from ' + event.data._sender_name : 'New message'),
                    description: event.data.content?.substring(0, 100),
                });
            }
        }
    }

    useEffect(() => {
        if (!jwt.initialized) {
            return;
        }

        pusher.subscribeTo('channel_user_' + (user?.uid || 'anonymous'));
        pusher.on(handler);

        return () => {
            pusher.off(handler);
            pusher.unsubscribeFrom('channel_user_' + (user?.uid || 'anonymous'));
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            return;
        }
        // Which sports they play (`plays`) comes with auth/me, not with a claim's or a login's own
        // answer: fetch it once before deciding on the racquet welcome.
        if (footballV2On(registry) && (user as any).plays === undefined && !playsLoadRef.current) {
            playsLoadRef.current = true;
            jwt.reloadUser();
            return;
        }

        // Organisers get one pop-up on entering the platform: "Will you be taking payments for your
        // event?" and the walk-through after it (PaymentsSetupWizard, onboarding/paymentsSetupPrompt) —
        // once a visit until they answer No or finish, and reopened when Stripe's form sends them back. The welcome
        // tutorial, the WhatsApp Sessions welcome and the mobile tour below are not shown to them (nor
        // the trial-ended offer or the quiz tour, which no longer open by themselves). Account
        // verification (a claimed account still to verify) comes first, as for anyone.
        if (+(user.role ?? 0) === UserRole.Organizer && !(user?.status && +user.status === UserStatus.ClaimedIncomplete)) {
            const search = new URLSearchParams(window.location.search);
            const backFromStripe = search.get(PAYMENTS_SETUP_RETURN_PARAM) === 'return';
            if (backFromStripe) {
                // Stripe's form sends them back here: pick the walk-through up after it, and tidy the address.
                search.delete(PAYMENTS_SETUP_RETURN_PARAM);
                const rest = search.toString();
                window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
            }
            const onPaymentsPage = window.location.pathname.startsWith('/go/payments')
                || (window.location.pathname.includes('/edit') && search.get('tab') === 'payments');
            // Not over a player profile either (their own, in Player View or not): it comes on the next page.
            const onPlayerProfile = window.location.pathname.startsWith('/profiles/');
            // The welcome cards (invite players, set up payments) pop up one at a time on every login
            // until each is actioned (onboarding/welcomeCards), and take this login's pop-up slot:
            // the payments walk-through waits for a login with nothing left to show, except when
            // Stripe's form sends them back, which always resumes it.
            let welcomeShown = welcomeCardsOpenedRef.current;
            try { welcomeShown = welcomeShown || !!sessionStorage.getItem(WELCOME_CARDS_SESSION_KEY); } catch { /* private mode */ }
            if (!backFromStripe && !onPaymentsPage && !onPlayerProfile && !welcomeCardsOpenedRef.current && welcomeCardsPopupDue(user as any).length) {
                welcomeCardsOpenedRef.current = true;
                try { sessionStorage.setItem(WELCOME_CARDS_SESSION_KEY, '1'); } catch { /* private mode: once for this page */ }
                globalModal.open(<OrganizerWelcomeModal onClose={() => globalModal.close()} />);
                return;
            }
            const due = backFromStripe || (!onPaymentsPage && !onPlayerProfile && !welcomeShown && !!paymentsSetupPromptDue(user as any));
            if (due && !paymentsPromptOpenedRef.current) {
                paymentsPromptOpenedRef.current = true;
                try { sessionStorage.setItem(PAYMENTS_PROMPT_SESSION_KEY, '1'); } catch { /* private mode: once for this page */ }
                globalModal.open(<PaymentsSetupWizard initialStep={backFromStripe ? 'checking' : 'ask'} onClose={() => globalModal.close()} />);
            }
            return;
        }

        // The club/community dashboard is a data screen, not a landing page: stacking the
        // Get-started modal, the driver.js tour AND the trial banner over it meant a first-time
        // organizer never saw the dashboard at all (Phase-2 walkthrough, fix 7). Popups stay
        // eligible for their normal surfaces on the next non-dashboard page.
        if (/^\/(clubs|communities)\/[^/]+\/(dashboard|overview)\/?$/.test(window.location.pathname)) {
            return;
        }

        // Race organizers are skipped: the tutorial is about creating a tournament or league,
        // neither of which exists in a race area — their next step is creating a race.
        const isRaceOrganizer = !!sportAreaForUser(user)?.isRaceArea;

        // Invited club managers are Players by role, but they didn't come here to play: they were
        // sent a link to run one club's page and squad. The rank/leaderboard welcome ("play matches
        // to earn coins, build streaks") is aimed at someone who signed up to compete, and reads as
        // a non-sequitur to them, so skip it and leave them on their club.
        const isClubManager = ((user as any)?.managed_clubs?.length ?? 0) > 0;

        // The coin/rank welcome is a racquet-sport feature: "play matches to earn coins, build
        // streaks, climb the rankings" describes the individual ladder that padel, tennis,
        // pickleball, badminton and squash players sign up for. In a gated sport area — football,
        // basketball, volleyball, cricket, esports, roundnet, running — people arrive to run or
        // play in a team competition and there is no personal ladder to introduce, so don't.
        // SPORT_AREAS holds exactly the non-racquet sports, so a defined area IS the test.
        const isSportArea = !!sportAreaForUser(user);

        // The setup checklist ("Getting started" in the sidebar) never opens on its own — it is a
        // button, so it cannot get in the way of whatever they came in to do.

        if (!isRaceOrganizer && user?.role !== UserRole.Player && user?.status && +user?.status !== UserStatus.Incomplete && +user?.status !== UserStatus.ClaimedIncomplete && !user?.meta?.welcome_tutorial_seen && !welcomeTutorialSeen && !welcomeModalOpenedRef.current) {
            welcomeModalOpenedRef.current = true;
            globalModal.open(<WelcomeTutorialModal onClose={() => {
                setWelcomeTutorialSeen('y');
                apiActionImproved('users-update', {
                    uid:    user!.uid,
                    values: {
                        version:                         (user!.version || 0) + 1,
                        ['meta->welcome_tutorial_seen']: true,
                    },
                }).then(() => jwt.reloadUser());
            }} />);
        } else if (user?.role === UserRole.Player && !isClubManager && !isSportArea && welcomeRankDue(user, registry) && !user?.meta?.welcome_rank_seen && !welcomeRankOpenedRef.current) {
            welcomeRankOpenedRef.current = true;
            globalModal.open(<WelcomeRankModal onClose={() => {
                apiActionImproved('users-update', {
                    uid:    user!.uid,
                    values: {
                        version:                      (user!.version || 0) + 1,
                        ['meta->welcome_rank_seen']: true,
                    },
                }).then(() => jwt.reloadUser());
            }} />);
        } else if (user?.status && +user?.status === UserStatus.ClaimedIncomplete) {
            globalModal.open(<ClaimProfileModal
                profile={user}
                initialState={"verification_pending"}
                onClaimed={() => {
                    document.location.reload();
                }} />);
        } else if ((user?.role === UserRole.Organizer || user?.role === UserRole.Master)
            && user?.whatsapp_sessions?.enabled && !user.whatsapp_sessions.welcome_seen && !whatsAppWelcomeOpenedRef.current
            && !window.location.pathname.startsWith('/sessions/') && !quizFocus) {
            // WhatsApp Sessions has arrived (the trial switch, Master → Activation): once, ever, for
            // every organizer, those here before it included — sport-area organizers (football, the
            // other team sports) among them, in their sport's words. On any page they load first,
            // the sport-area dashboard included, but not on the page itself (they are there) and not
            // over the quiz's own welcome on the draw an organizer lands on straight from sign-up:
            // it comes on the next page instead (quizFocus is a dependency for that).
            whatsAppWelcomeOpenedRef.current = true;
            globalModal.open(<WhatsAppSessionsWelcome mode={user.whatsapp_sessions.mode} format={user.whatsapp_sessions.format} onClose={() => {
                markWhatsAppSessionsSeen('welcome').then(() => jwt.reloadUser());
            }} />);
        } else if (!isClubManager && user?.status && +user?.status !== UserStatus.Incomplete && !user?.meta?.welcome_tour_seen && !welcomeTourShown) {
            showWelcomeTour();
        }
    }, [user, isSubscribed, quizFocus]);

    useEffect(() => {
        if (versionQuery.data && versionQuery.data !== config.version && config.appEnv !== 'local' && !config.isCordova && !document.location.hostname.includes(/* PR testing domain */'-pr.')) {
            addToast({
                timeout:    0,
                title:      `New version is available: ${versionQuery.data}`,
                color:      "warning",
                endContent: <PButton color={"primary"} onPress={() => document.location.reload()}>Update</PButton>,
            });
        }
    }, [versionQuery.data]);

    const {isRefreshing, pullPosition} = usePullToRefresh({
        onRefresh:         () => {
            queryClient.clear()
            if (dayjs().unix() - lastReloadTs <= 2) {
                document.location.reload();
            }
            setLastReloadTs(dayjs().unix())
        },
        maximumPullLength: MAXIMUM_PULL_LENGTH,
        refreshThreshold:  REFRESH_THRESHOLD,
        isDisabled:        !config.isCordova,
    });

    function showWelcomeTour() {
        if (!isMobile) {
            return false;
        }
        setWelcomeTourShown(true);

        setTimeout(() => { // wait for UI to settle
            const driverObj = driver({
                showProgress: true,
                steps:        [
                    {
                        element: '#HeaderSidebarTrigger',
                        popover: {
                            title:       'Main Menu',
                            description: 'Click here to open the main menu.',
                            side:        "left",
                            align:       'start',
                            /*onNextClick: () => {
                                router.navigate({to: '/leagues/'});
                                driverObj.moveNext();
                            }*/
                        }
                    },
                    {popover: {title: 'Great', description: 'Use the menu to navigate the platform'}}
                ],
                onDestroyed:  () => {
                    apiActionImproved('users-update', {
                        uid:    user!.uid,
                        values: {
                            version:                     user!.version + 1,
                            ['meta->welcome_tour_seen']: true,
                        },
                    }).then(() => jwt.reloadUser());
                }
            });

            driverObj.drive();
        }, 1000);
    }

    if (!jwt.initialized) {
        return <>
            {/*<Header showBackUrl={false} />*/}

            <div className="text-center mt-6">
                <Spinner size={"lg"} className="mx-auto"></Spinner>
            </div>
        </>
    }

    if (!user) {
        return loginRedirect;
    }

    // Sport-area users are confined to their area (football, basketball, …) while logged in — any
    // other in-app route bounces back to it. Account/payment pages (profile, wallet, billing) and
    // club management (/clubs/… — their public club page editor) stay reachable so Stripe
    // onboarding and club-page editing still work. (Logging out exits the area.)
    const confinedArea = sportAreaForUser(user);
    if (confinedArea) {
        const path = location.pathname;
        const isAccountPath = path.startsWith('/profiles/') || path.startsWith('/billing') || path.endsWith('/billing')
            || path.startsWith('/clubs/') || path.startsWith('/communities/') // own club/community profile + club-page editor
            || path.startsWith('/sessions/') // WhatsApp Sessions: one page for every sport, the team-sport areas included
            || path.startsWith('/go/'); // /go/… shortcuts (e.g. /go/payments) resolve to an account page
        // League of Leagues ranks their leagues too, so their organizers can open it (race areas
        // have no leagues; team-sport players have no leaderboard).
        const isLeaderboardPath = path.startsWith('/earn') && !confinedArea.isRaceArea && user.role !== UserRole.Player;
        if (!path.startsWith(confinedArea.basePath + '/') && !isAccountPath && !isLeaderboardPath) {
            // An event's racquet address (an older Event Pass checkout return, an old shared
            // link) is the SAME page in the area — translate it, query string and all, rather
            // than dropping the organizer on the area home. Dropping it also dropped
            // ?event_pass=success&session_id=…, so a paid pass was never confirmed.
            // Race areas have no tournament or league pages, so they keep the home bounce.
            if (!confinedArea.isRaceArea && /^\/(tournaments|leagues)\/[^/]+/.test(path)) {
                window.location.replace(`${confinedArea.basePath}${path}${window.location.search}${window.location.hash}`);
                return null;
            }
            return <Navigate to={sportAreaHome(confinedArea) as any} replace />;
        }
    }

    // Club managers get their own portal, so keep them in it: anything outside their club and their
    // own account bounces back. Mirrors the sport-area confinement above — without it the player
    // routes stay reachable by URL even though nothing links to them any more.
    if ((user as any)?.is_club_manager_only) {
        const path = location.pathname;
        const managedClubs = (user as any).managed_clubs ?? [];
        const allowed = path.startsWith('/clubs/') || path.startsWith('/profiles/')
            || path.startsWith('/messages') || path.startsWith('/club-invite/') || path.startsWith('/go/');
        if (!allowed && managedClubs[0]?.uid) {
            return <Navigate to={`/clubs/${managedClubs[0].uid}/manage/` as any} replace />;
        }
    }

    // The racquet questionnaire: not for a player with nothing racquet about them (a football
    // player who has claimed their profile goes straight to it).
    if (+(user?.status || 0) === UserStatus.Incomplete && racquetOnboarding(user, registry)) {
        return <>
            <Header isPublicPage={true} showBackUrl={false} />

            <User_ProfileView_PlayerQuestionnaire />
        </>
    }

    if (+(user?.status || 0) === UserStatus.ClaimedIncomplete) {
        return <>
            <Header isPublicPage={true} showBackUrl={false} />

            <div className={"flex justify-center mt-8"}>Please check your inbox and click the link to verify your email.</div>
        </>
    }

    return (
        <>
            <title>Tournament & League Management Software | ScorePal</title>
            <EmailRequiredBanner />

            <div
                style={{
                    top:     (isRefreshing ? REFRESH_THRESHOLD : pullPosition) / 3,
                    opacity: isRefreshing || pullPosition > 0 ? 1 : 0,
                }}
                className={'bg-default-100 fixed inset-x-1/2 z-30 h-12 w-12 -translate-x-1/2 rounded-full p-2 shadow'}
            >
                <div
                    className={`h-full w-full ${isRefreshing ? `animate-spin` : ``}`}
                    style={!isRefreshing ? {transform: `rotate(${pullPosition}deg)`} : {}}
                >
                    <Icon icon={"material-symbols-light:refresh"} className={"h-full w-full"} />
                </div>
            </div>

            <SidebarProvider defaultOpen={true}>
                {/* One-off path: any request the Event Pass gate refuses opens the offer — from
                    every scoring screen, every time, even after the offer was dismissed. */}
                <PassGateListener />
                {/* Every organiser sees the sidebar, quiz arrivals too (Oct 2026): what the app
                    offers is in front of them from the first page. The quiz arrival keeps its
                    one-time tour. */}
                <AppSidebar />
                {quizFocus && <QuizWelcomeTour />}
                <SidebarInset className={"bg-[#EDEEF4] dark:bg-zinc-900"}>

                    {/*{config.isProduction() && <AdSensePush
                        key={"ad-header-" + location.pathname}>
                        <ins className="adsbygoogle"
                             style={{display: "block", textAlign: "center", width: "100%"}}
                             data-ad-client="ca-pub-6471700087527589"
                             data-ad-slot="1327544326"
                             data-ad-format="auto"
                             data-full-width-responsive="true"></ins>
                    </AdSensePush>}
                    {config.isTestMode && <div className={"w-full h-20 bg-red-200 flex items-center justify-center inset-shadow-sm border-2 border-red-500"}>
                        AD
                    </div>}*/}

                    {/* Trial / past-due / frozen. Renders nothing unless one applies. */}
                    {/* A master signed in as this user: whose account it is and the way back. */}
                    <ImpersonationBanner />
                    <BillingStatusBanner />
                    {/* An Event Pass checkout return is confirmed (and applied) on any page. */}
                    <EventPassReturnConfirm />
                    {/* The one-question organizer-path screen is asked where an event is created
                        (useOrganizerPathQuestion in the create entry points), never from the shell. */}
                    <Outlet />
                    {/* WhatsApp support: floats bottom-right on every signed-in page, hidden when
                        no number is configured on Master → Activation. */}
                    <SupportChatButton />

                    {config.isProduction() && <AdSensePush
                        key={"ad-footer-" + location.pathname}>
                        <ins className="adsbygoogle"
                             style={{display: "block", textAlign: "center", width: "100%"}}
                             data-ad-client="ca-pub-6471700087527589"
                             data-ad-slot="2589089873"
                             data-ad-format="auto"
                             data-full-width-responsive="true"></ins>
                    </AdSensePush>}
                    {config.isTestMode && <div className={"w-full h-20 bg-red-200 flex items-center justify-center inset-shadow-sm border-2 border-red-500"}>
                        AD
                    </div>}

                </SidebarInset>
            </SidebarProvider>
        </>
    );
}
